import { createBrowserRouter, Navigate } from 'react-router-dom'
import { ProtectedRoute } from '@/components/protected-route'
import { AccessGuard } from '@/components/access-guard'
import { AppLayout } from '@/components/layout/app-layout'
import LoginPage from '@/pages/login'
import SignupPage from '@/pages/signup'
import BillingPage from '@/pages/billing'
import DashboardPage from '@/pages/dashboard'
import LeadsPage from '@/pages/leads'
import LeadDiscoverPage from '@/pages/lead-discover'
import CampaignsPage from '@/pages/campaigns'
import ContentPage from '@/pages/content'
import InboxPage from '@/pages/inbox'
import SettingsPage from '@/pages/settings'
import SupportPage from '@/pages/support'
import AffiliatePage from '@/pages/affiliate'
import NotFoundPage from '@/pages/not-found'
import WaitlistPage from '@/pages/waitlist'
import OnboardingWizard from '@/features/onboarding/onboarding-wizard'
import { SuperAdminApp } from '@/features/admin/super-admin'
import { TeamApp } from '@/features/team/team-console'

export const router = createBrowserRouter([
  // Public routes — signup works but non-allowed emails see "Coming Soon" after login
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/signup',
    element: <SignupPage />,
  },

  // Protected routes wrapped in AppLayout
  {
    element: (
      <ProtectedRoute>
        <AccessGuard>
          <AppLayout />
        </AccessGuard>
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: '/dashboard',
        element: <DashboardPage />,
      },
      {
        path: '/leads',
        element: <LeadsPage />,
      },
      {
        path: '/leads/discover',
        element: <LeadDiscoverPage />,
      },
      {
        path: '/campaigns',
        element: <CampaignsPage />,
      },
      {
        path: '/content',
        element: <ContentPage />,
      },
      {
        path: '/inbox',
        element: <InboxPage />,
      },
      {
        path: '/settings',
        element: <SettingsPage />,
      },
      {
        path: '/support',
        element: <SupportPage />,
      },
      {
        path: '/affiliate',
        element: <AffiliatePage />,
      },
      // Client team console (Enterprise teams): org managers only; the page
      // itself checks /api/team/me and the leads worker enforces the role.
      {
        path: '/team/*',
        element: <TeamApp />,
      },
    ],
  },

  // Billing / payment wall — protected, gated, no AppLayout sidebar.
  // Reached after onboarding when the user isn't yet entitled.
  {
    path: '/billing',
    element: (
      <ProtectedRoute>
        <AccessGuard>
          <BillingPage />
        </AccessGuard>
      </ProtectedRoute>
    ),
  },

  // Onboarding — protected, gated, no AppLayout sidebar. First stop after signup.
  {
    path: '/onboarding',
    element: (
      <ProtectedRoute>
        <AccessGuard>
          <OnboardingWizard />
        </AccessGuard>
      </ProtectedRoute>
    ),
  },

  // Legacy waitlist URL — kept so old links/emails don't 404. The waitlist
  // flow is retired (WAITLIST_MODE=false); AccessGuard never routes here.
  {
    path: '/waitlist',
    element: (
      <ProtectedRoute>
        <WaitlistPage />
      </ProtectedRoute>
    ),
  },

  // Super-admin (DCA staff only) — separate dashboard with green
  // matrix-rain. Standalone shell, NOT inside AppLayout, so the staff
  // view is visually distinct from the customer dashboard.
  {
    path: '/super-admin/*',
    element: (
      <ProtectedRoute>
        <SuperAdminApp />
      </ProtectedRoute>
    ),
  },

  // Catch-all
  {
    path: '*',
    element: <NotFoundPage />,
  },
])
